import { Family, FamilyEvent, FamilyRules } from '../types/family';
import {
  beddingTask,
  careTask,
  eveningTask,
  houseTask,
  instrumentTask,
  learningTask,
  planItem,
  readingTask,
  roomDayTask,
  roomWeekTask,
  sportTask,
} from '../utils/templateBuilders';

export { DEMO_DATE } from './demo';

// Vse spodaj so označeni vzorčni podatki, vezani na identifikator posamezne družine.
// Skrbnik jih ureja v Nastavitvah družine; nobena vrednost ni globalno pravilo.

const daily = (times: number) => ({ kind: 'dnevno' as const, times });
const everyN = (n: number) => ({ kind: 'vsakihNDni' as const, n });

// ---------- Družina Siter (prva dejanska družina, začetni podatki) ----------

const siterRules: FamilyRules = {
  readingMinutes: 30,
  basicThresholdPct: 75,
  allowanceAmount: 5,
  currency: '€',
  payoutDay: 'nedelja',
  payoutTime: '19.00',
  sportMinutes: 45,
  sportPerWeekChild: 4,
  sportPerWeekChildWithSchoolPE: 2,
  sportPerWeekAdult: 4,
  houseCategoryValue: 30,
  roomBonus: 1,
  roomBonusWeeklyMax: 6,
  extraMinutesStep: 15,
  extraMinutesBonus: 2,
  extraDailyMax: 4,
  lessonDayBonus: 5,
  evening: {
    schoolNightRoom: '21.30',
    weekendRoom: '22.30',
    weekendSleep: '23.00',
    devicesPlace: 'zaklenjen predalnik ali polnjenje v spalnici staršev',
  },
  reminders: { dailySummary: '18.30', eveningMinutesBefore: 15 },
  visibility: { calendarAll: true, personalParentsOnly: true },
};

const adults = ['timotej', 'barbara'];
const kids = ['eli', 'irenej', 'ema', 'jona', 'jakob'];
const all = [...adults, ...kids];

// Hranjenje vedno vključuje tudi svežo vodo – ni ločene naloge za vodo.
const FED = { description: 'Vključuje svežo vodo.' };
const FED_AM = { ...FED, partOfDay: 'jutro' as const };
const FED_PM = { ...FED, partOfDay: 'vecer' as const };
const AM = { partOfDay: 'jutro' as const };
const PM = { partOfDay: 'vecer' as const };

// Stalni tedenski urnik glasbene šole in plavanja (enak vsak teden).
const weeklyActivities: { day: number; title: string; start: string; end: string; who: string; type: FamilyEvent['type'] }[] = [
  { day: 0, title: 'Jakob – teorija', start: '15.00', end: '16.00', who: 'jakob', type: 'solfeggio' },
  { day: 0, title: 'Jona – teorija', start: '16.00', end: '17.00', who: 'jona', type: 'solfeggio' },
  { day: 0, title: 'Jona – violina', start: '17.00', end: '18.00', who: 'jona', type: 'pouk-instrumenta' },
  { day: 1, title: 'Jona – orkester', start: '16.00', end: '18.00', who: 'jona', type: 'orkester' },
  { day: 1, title: 'Jakob – bas', start: '16.00', end: '16.30', who: 'jakob', type: 'pouk-instrumenta' },
  { day: 1, title: 'Jakob – plavanje', start: '17.00', end: '18.00', who: 'jakob', type: 'sport' },
  { day: 1, title: 'Jona – violina', start: '18.00', end: '18.30', who: 'jona', type: 'pouk-instrumenta' },
  { day: 2, title: 'Jakob – plavanje', start: '16.00', end: '17.00', who: 'jakob', type: 'sport' },
  { day: 2, title: 'Jona – plavanje', start: '16.00', end: '17.00', who: 'jona', type: 'sport' },
  { day: 3, title: 'Jakob – bas', start: '16.00', end: '16.30', who: 'jakob', type: 'pouk-instrumenta' },
  { day: 3, title: 'Jona – plavanje', start: '16.30', end: '17.30', who: 'jona', type: 'sport' },
  { day: 3, title: 'Jakob – plavanje', start: '17.00', end: '18.00', who: 'jakob', type: 'sport' },
];
const dayNames = ['vsak ponedeljek', 'vsak torek', 'vsako sredo', 'vsak četrtek'];
const weeklyEvents = (mondays: number[]): FamilyEvent[] =>
  mondays.flatMap((mon) =>
    weeklyActivities.map((a, i) => ({
      id: `w-${mon}-${i}`,
      title: a.title,
      date: `2026-10-${String(mon + a.day).padStart(2, '0')}`,
      start: a.start,
      end: a.end,
      participantIds: [a.who],
      source: 'google-druzinski' as const,
      type: a.type,
      repeat: dayNames[a.day],
    }))
  );

const siter: Family = {
  id: 'siter',
  name: 'Siter',
  sampleLabel: 'Vzorčni začetni podatki',
  members: [
    { id: 'timotej', name: 'Timotej', role: 'skrbnik', color: '#2F5D9A', invite: 'sprejeto' },
    { id: 'barbara', name: 'Barbara', role: 'skrbnik', color: '#7B4FB0', invite: 'sprejeto' },
    { id: 'eli', name: 'Eli', role: 'otrok', color: '#C2501F', birth: '2009-03', invite: 'sprejeto' },
    { id: 'irenej', name: 'Irenej', role: 'otrok', color: '#2E7D4F', birth: '2010-08', invite: 'sprejeto' },
    { id: 'ema', name: 'Ema', role: 'otrok', color: '#B23A6B', birth: '2011-09-16', invite: 'sprejeto' },
    { id: 'jona', name: 'Jona', role: 'otrok', color: '#8A5A00', birth: '2013-05-21', invite: 'sprejeto' },
    { id: 'jakob', name: 'Jakob', role: 'otrok', color: '#1F6F80', birth: '2015-09-15', invite: 'poslano', contact: 'jakob@primer.si' },
  ],
  modules: {
    hisnaOpravila: true,
    osebnaRutina: true,
    sola: true,
    zivali: true,
    glasba: true,
    vecerniRezim: true,
    nagrade: true,
    skupniSeznami: true,
  },
  rules: siterRules,
  animals: [
    { id: 'toffee', name: 'Toffee', species: 'pes', caretakerIds: ['eli'], confirmed: false, note: 'Povezava z Eli je delovna domneva.' },
    { id: 'dihurcek', name: 'Dihurček', species: 'dihur', caretakerIds: ['jona'], habitat: 'Kletka dihurčka', confirmed: true },
    { id: 'polh-1', name: 'Polh 1', species: 'afriški polh', caretakerIds: ['jakob'], habitat: 'Skupna kletka polhov', confirmed: true },
    { id: 'polh-2', name: 'Polh 2', species: 'afriški polh', caretakerIds: ['jakob'], habitat: 'Skupna kletka polhov', confirmed: true },
    { id: 'ori', name: 'Ori', species: 'mačka', caretakerIds: ['barbara'], confirmed: true },
    { id: 'milky', name: 'Milky', species: 'mačka', caretakerIds: ['irenej'], confirmed: true },
    { id: 'onyx', name: 'Onyx', species: 'pes', caretakerIds: [], confirmed: true, note: 'Sprotni dogovor – kdor utegne.' },
    { id: 'kunec', name: 'Kunec', species: 'kunec', caretakerIds: [], habitat: 'Kletka kunca', confirmed: true, note: 'Sprotni dogovor – kdor utegne.' },
  ],
  templates: [
    ...all.map((id) => readingTask(id, siterRules.readingMinutes)),
    ...all.map((id) => sportTask(id, siterRules.sportMinutes)),
    instrumentTask('vadba-violina-jona', 'Violina – domača vadba', 'jona'),
    instrumentTask('vadba-bas-jakob', 'Bas kitara – domača vadba', 'jakob'),
    ...kids.map(learningTask),
    // Živali – osebne
    careTask('toffee-hrana-1', 'Toffee – hranjenje zjutraj', ['eli'], ['toffee'], 1, daily(2), FED_AM),
    careTask('toffee-hrana-2', 'Toffee – hranjenje zvečer', ['eli'], ['toffee'], 1, daily(2), FED_PM),
    careTask('toffee-sprehod-1', 'Toffee – sprehod zjutraj', ['eli'], ['toffee'], 3, daily(2), AM),
    careTask('toffee-sprehod-2', 'Toffee – sprehod zvečer', ['eli'], ['toffee'], 3, daily(2), PM),
    careTask('toffee-oci', 'Toffee – čiščenje izcedka iz oči', ['eli'], ['toffee'], 1, daily(1)),
    careTask('toffee-cesanje', 'Toffee – česanje', ['eli'], ['toffee'], 2, everyN(2), { nextDue: '2026-10-09' }),
    careTask('toffee-frizer', 'Toffee – dogovor s pasjim frizerjem', ['eli'], ['toffee'], 0, { kind: 'dogovor' }, { nextDue: '2026-10-24', valueType: undefined, planKey: undefined }),
    careTask('dihur-hrana-1', 'Dihurček – hranjenje zjutraj', ['jona'], ['dihurcek'], 1, daily(2), FED_AM),
    careTask('dihur-hrana-2', 'Dihurček – hranjenje zvečer', ['jona'], ['dihurcek'], 1, daily(2), FED_PM),
    careTask('dihur-stranisca', 'Dihurček – stranišča', ['jona'], ['dihurcek'], 2, daily(1)),
    careTask('polha-hrana', 'Polha – hranjenje (skupaj)', ['jakob'], ['polh-1', 'polh-2'], 1, daily(1), FED),
    careTask('polha-kletka', 'Kletka polhov – čiščenje', ['jakob'], ['polh-1', 'polh-2'], 8, everyN(14), { nextDue: '2026-10-14', confirmation: 'skrbnik' }),
    careTask('milky-hrana', 'Milky – hranjenje', ['irenej'], ['milky'], 1, daily(1), FED),
    careTask('milky-stranisce', 'Milky – mačje stranišče', ['irenej'], ['milky'], 3, everyN(3), { nextDue: '2026-10-09' }),
    careTask('ori-hrana', 'Ori – hranjenje', ['barbara'], ['ori'], 1, daily(1), FED),
    careTask('ori-stranisce', 'Ori – mačje stranišče', ['barbara'], ['ori'], 3, everyN(3), { nextDue: '2026-10-10' }),
    // Živali – sprotni dogovor
    careTask('onyx-hrana-1', 'Onyx – hranjenje zjutraj', [], ['onyx'], 1, daily(2), FED_AM),
    careTask('onyx-hrana-2', 'Onyx – hranjenje zvečer', [], ['onyx'], 1, daily(2), FED_PM),
    careTask('onyx-sprehod-1', 'Onyx – sprehod zjutraj', [], ['onyx'], 3, daily(2), AM),
    careTask('onyx-sprehod-2', 'Onyx – sprehod zvečer', [], ['onyx'], 3, daily(2), PM),
    careTask('kunec-hrana', 'Kunec – hranjenje', [], ['kunec'], 1, daily(1), FED),
    careTask('kunec-kletka', 'Kletka kunca – čiščenje', [], ['kunec'], 8, everyN(14), { nextDue: '2026-10-20', confirmation: 'skrbnik' }),
    // Hiša
    houseTask('kuhinja', 'Kuhinja', ['jona', 'jakob'], siterRules.houseCategoryValue, 'pult prazen in obrisan, pomivalni stroj izpraznjen.'),
    houseTask('kopalnice', 'Kopalnice', ['irenej', 'ema'], siterRules.houseCategoryValue, 'umivalnik in ogledalo čista, brisače obešene.'),
    houseTask('hodniki', 'Hodniki', ['eli'], siterRules.houseCategoryValue, 'čevlji na polici, tla brez stvari.'),
    houseTask('dnevna-soba', 'Dnevna soba in jedilnica', ['barbara', 'ema'], siterRules.houseCategoryValue, 'mize proste, stvari pospravljene na svoje mesto.'),
    houseTask('sesanje', 'Sesanje', ['timotej', 'irenej'], siterRules.houseCategoryValue, 'skupni prostori brez drobtin in dlak.'),
    houseTask('perilo', 'Perilo', ['barbara', 'eli'], siterRules.houseCategoryValue, 'koš ni prepoln, suho perilo v 24 urah zloženo in pospravljeno.'),
    houseTask('okolica', 'Okolica hiše', ['timotej', 'jakob'], siterRules.houseCategoryValue, 'dvorišče in vhod pospravljena, smeti odnesene.'),
    ...kids.map(roomWeekTask),
    ...kids.map((id) => roomDayTask(id, siterRules.roomBonus)),
    ...kids.map((id) => beddingTask(id, '2026-10-31')),
    ...kids.map(eveningTask),
  ],
  plans: [
    { memberId: 'timotej', items: [planItem('branje', 'Branje', 5, 7, 3), planItem('sport', 'Šport', 5, 4, 2)] },
    {
      memberId: 'barbara',
      items: [planItem('branje', 'Branje', 5, 7, 4), planItem('sport', 'Šport', 5, 4, 3), planItem('zivali', 'Oskrba živali', 1, 16, 10, { weighted: true })],
    },
    {
      memberId: 'eli',
      items: [
        planItem('branje', 'Branje', 5, 7, 3),
        planItem('sport', 'Šport', 5, 4, 1),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 3),
        planItem('zivali', 'Oskrba živali', 1, 71, 37, { weighted: true }),
        planItem('vecer', 'Večerna rutina', 2, 7, 4),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
    {
      memberId: 'irenej',
      items: [
        planItem('branje', 'Branje', 5, 7, 4),
        planItem('sport', 'Šport', 5, 4, 2),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 4),
        planItem('zivali', 'Oskrba živali', 1, 16, 8, { weighted: true }),
        planItem('vecer', 'Večerna rutina', 2, 7, 4),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
    {
      memberId: 'ema',
      items: [
        planItem('branje', 'Branje', 5, 7, 2),
        planItem('sport', 'Šport', 5, 4, 1),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 2),
        planItem('vecer', 'Večerna rutina', 2, 7, 3),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
    {
      memberId: 'jona',
      items: [
        planItem('branje', 'Branje', 5, 7, 4),
        planItem('instrument', 'Violina – domača vadba', 5, 5, 2, { hint: 'vsak dan razen pon in tor (pouk violine)' }),
        planItem('sport', 'Šport', 5, 4, 0),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 4),
        planItem('zivali', 'Oskrba živali', 1, 28, 14, { weighted: true }),
        planItem('vecer', 'Večerna rutina', 2, 7, 4),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
    {
      memberId: 'jakob',
      items: [
        planItem('branje', 'Branje', 5, 7, 3),
        planItem('instrument', 'Bas kitara – domača vadba', 5, 5, 2, { hint: 'vsak dan razen tor in čet (pouk basa)' }),
        planItem('sport', 'Šport', 5, 4, 0),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 3),
        planItem('zivali', 'Oskrba živali', 1, 7, 4, { weighted: true }),
        planItem('vecer', 'Večerna rutina', 2, 7, 4),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
  ],
  events: [
    ...weeklyEvents([5, 12]),
    { id: 'e-viva', title: 'Zbor VIVA – vaja', date: '2026-10-07', start: '19.30', participantIds: adults, source: 'google-viva', type: 'zbor', repeat: 'vsako sredo' },
    { id: 'e-viva-2', title: 'Zbor VIVA – vaja', date: '2026-10-14', start: '19.30', participantIds: adults, source: 'google-viva', type: 'zbor', repeat: 'vsako sredo' },
  ],
  rewards: [
    { id: 'r-igra', title: 'Izbira naslednje družabne igre', cost: 20 },
    { id: 'r-film', title: 'Izbira filma na filmskem večeru', cost: 30 },
    { id: 'r-prednost', title: 'Prednost pri naslednjem terminu igranja', cost: 30 },
    { id: 'r-30', title: 'Dodatnih 30 minut igranja', cost: 40 },
    { id: 'r-60', title: 'Dodatnih 60 minut igranja', cost: 70 },
  ],
  calendars: [
    { id: 'g-druzinski', name: 'Družinski Google koledar', source: 'google-druzinski', memberIds: all, status: 'vzorec' },
    { id: 'g-viva', name: 'Zbor VIVA', source: 'google-viva', memberIds: adults, status: 'vzorec' },
  ],
  bonusSeed: { eli: 36, irenej: 18, ema: 27, jona: 55, jakob: 12, timotej: 0, barbara: 0 },
  lastWeekReached: { eli: true, irenej: true, ema: false, jona: true, jakob: true },
  goal: { title: 'Izlet na Pohorje', reward: 'Celodnevni družinski izlet z žičnico', target: 400, current: 262 },
};

// ---------- Izmišljena družina B (samo za preizkus prilagodljivosti) ----------

const kovacRules: FamilyRules = {
  ...siterRules,
  readingMinutes: 20,
  allowanceAmount: 3,
  evening: { ...siterRules.evening, devicesPlace: 'polnilna postaja v dnevni sobi' },
};

const kovac: Family = {
  id: 'kovac',
  name: 'Kovač',
  sampleLabel: 'Izmišljena družina B – samo za preizkus',
  members: [
    { id: 'ana', name: 'Ana', role: 'skrbnik', color: '#2F5D9A', invite: 'sprejeto' },
    { id: 'marko', name: 'Marko', role: 'skrbnik', color: '#7B4FB0', invite: 'sprejeto' },
    { id: 'lina', name: 'Lina', role: 'otrok', color: '#C2501F', invite: 'sprejeto' },
    { id: 'tim', name: 'Tim', role: 'otrok', color: '#2E7D4F', invite: 'sprejeto' },
  ],
  modules: {
    hisnaOpravila: true,
    osebnaRutina: true,
    sola: true,
    zivali: false,
    glasba: false,
    vecerniRezim: false,
    nagrade: true,
    skupniSeznami: true,
  },
  rules: kovacRules,
  animals: [],
  templates: [
    ...['ana', 'marko', 'lina', 'tim'].map((id) => readingTask(id, kovacRules.readingMinutes)),
    ...['ana', 'marko', 'lina', 'tim'].map((id) => sportTask(id, kovacRules.sportMinutes)),
    learningTask('lina'),
    learningTask('tim'),
    houseTask('kuhinja', 'Kuhinja', ['lina', 'tim'], kovacRules.houseCategoryValue),
    houseTask('kopalnica', 'Kopalnica', ['ana'], kovacRules.houseCategoryValue),
    houseTask('perilo', 'Perilo', ['marko'], kovacRules.houseCategoryValue),
    roomWeekTask('lina'),
    roomWeekTask('tim'),
  ],
  plans: [
    { memberId: 'ana', items: [planItem('branje', 'Branje', 5, 7, 4), planItem('sport', 'Šport', 5, 4, 2)] },
    { memberId: 'marko', items: [planItem('branje', 'Branje', 5, 7, 2), planItem('sport', 'Šport', 5, 4, 2)] },
    {
      memberId: 'lina',
      items: [
        planItem('branje', 'Branje', 5, 7, 3),
        planItem('sport', 'Šport', 5, 4, 1),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 3),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
    {
      memberId: 'tim',
      items: [
        planItem('branje', 'Branje', 5, 7, 4),
        planItem('sport', 'Šport', 5, 4, 2),
        planItem('ucenje', 'Domače naloge in učni plan', 5, 5, 4),
        planItem('soba', 'Soba – sobotni pregled', 15, 1, 0, { hint: 'v soboto' }),
      ],
    },
  ],
  events: [
    { id: 'k-plavanje', title: 'Plavanje', date: '2026-10-09', start: '17.00', participantIds: ['tim'], driverId: 'marko', source: 'app', type: 'sport' },
    { id: 'k-druzinsko', title: 'Družabne igre', date: '2026-10-10', start: '19.00', participantIds: [], source: 'app', type: 'druzinsko' },
  ],
  rewards: [
    { id: 'k-film', title: 'Izbira filma', cost: 25 },
    { id: 'k-30', title: 'Dodatnih 30 minut igranja', cost: 35 },
  ],
  calendars: [],
  bonusSeed: { lina: 9, tim: 14 },
  lastWeekReached: { lina: true, tim: false },
  goal: null,
};

export const families: Family[] = [siter, kovac];
